'use client';
import { useState, useMemo, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import useMediaQuery from '@/lib/useMediaQuery';
import { SM_BREAKPOINT } from '@/lib/constants';
import CustomerCard from '@/components/CustomerCard';
import DropdownSelect from '@/components/DropdownWithCheckboxes';
import customerData from '@/sections/customers/customerData';

const filterByOptions: string[] = Array.from(new Set(customerData.map(customer => customer.industry)));

const sortByOptions: string[] = ['Recently Added', 'A-Z', 'Z-A'];

export const ExploreMoreCustomersSection: React.FC = () => {
  const [endIdx, setEndIdx] = useState(9);
  const [sortByArray, setSortByArray] = useState<string[]>([]);
  const [filterArray, setFilterArray] = useState<string[]>([]);

  const isMobile = useMediaQuery(`(max-width: ${SM_BREAKPOINT}px)`);

  const loadMoreNumber = isMobile ? 3 : 9;

  const handleLoadMore = () => {
    if (endIdx + loadMoreNumber > customerData.length - 1) {
      setEndIdx(customerData.length - 1);
    } else {
      setEndIdx(prev => prev + loadMoreNumber);
    }
  };

  const handleLoadLess = () => {
    setEndIdx(loadMoreNumber);
  };

  useEffect(() => {
    setEndIdx(loadMoreNumber);
  }, [isMobile, loadMoreNumber]);

  const handleSortAndFilter = useMemo(() => {
    let filteredCustomerDataCopy = customerData.filter(customer => {
      if (filterArray.length === 0) {
        return true;
      }
      return filterArray.includes(customer.industry);
    });

    filteredCustomerDataCopy =
      sortByArray.length === 0
        ? filteredCustomerDataCopy.sort((a, b) => b.date - a.date)
        : sortByArray[0] === 'Recently Added'
          ? filteredCustomerDataCopy.sort((a, b) => b.date - a.date)
          : sortByArray[0] === 'A-Z'
            ? filteredCustomerDataCopy.sort((a, b) => a.name.localeCompare(b.name))
            : filteredCustomerDataCopy.sort((a, b) => b.name.localeCompare(a.name));

    return filteredCustomerDataCopy;
  }, [filterArray, sortByArray]);

  return (
    <section
      id="CustomersMeetOurCustomersSection"
      className="py-section m-auto flex w-fit flex-col gap-8 bg-white px-4 text-black md:gap-12 md:px-10 lg:gap-12 lg:px-20 xl:gap-12 xl:px-20"
    >
      <h3 className="text-h3 text-center">Explore more customers</h3>
      <div className="flex w-full flex-col justify-between gap-4 md:flex-row lg:flex-row xl:flex-row">
        <DropdownSelect
          options={filterByOptions}
          hasClearAll
          isMultiSelect
          placeholder="Filter by Industry"
          selectedOptions={filterArray}
          selectionSummary={`Industr${filterArray?.length > 1 ? 'ies' : 'y'} selected`}
          setSelectedOptions={setFilterArray}
        />
        <DropdownSelect
          options={sortByOptions}
          placeholder="Sort by"
          selectedOptions={sortByArray}
          setSelectedOptions={setSortByArray}
        />
      </div>
      <div className="grid grid-cols-1 gap-8 self-center md:grid-cols-2 md:gap-6 lg:grid-cols-3 lg:gap-8 xl:grid-cols-3 xl:gap-8">
        {handleSortAndFilter.slice(0, endIdx).map((customer, idx) => (
          <CustomerCard
            key={`${customer.name}_${idx}`}
            icon={customer.icon}
            text={customer.text}
            targetText={customer.targetText}
            target={customer.target}
          />
        ))}
      </div>
      {handleSortAndFilter.length < endIdx ? null : (
        <>
          {handleSortAndFilter.length - 1 === endIdx ? (
            <button onClick={handleLoadLess} className="group flex flex-row items-center justify-center gap-2">
              Show less <ChevronUpIcon className="size-4 transition-all group-hover:-translate-y-0.5" />
            </button>
          ) : (
            <button onClick={handleLoadMore} className="group flex flex-row items-center justify-center gap-2">
              Load more <ChevronDownIcon className="size-4 transition-all group-hover:-translate-y-0.5" />
            </button>
          )}
        </>
      )}
    </section>
  );
};
